import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Clock, ArrowLeft, RefreshCw, Loader2, Users, Timer, Monitor, Globe, Smartphone,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useRestaurant } from '../context/RestaurantContext';
import deviceService from '../services/deviceService';

/* One terminal's staff-session ledger as a full page, with per-staff totals and shifts grouped by day. */

const fmtDuration = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const time = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

// The session's calendar day, in the reader's timezone.
const dayKey = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const dayLabel = (key) => {
  if (key === '—') return 'Unknown day';
  const d = new Date(`${key}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 864e5);
  const base = d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (diff === 0) return `Today · ${base}`;
  if (diff === 1) return `Yesterday · ${base}`;
  return base;
};

const CLIENT_LABEL = { POS: 'POS', ANDROID: 'Android', WEB: 'Web' };
// What the register calls each kind of machine, in the words staff use.
const DEVICE_LABEL = {
  POS: 'POS terminal', ANDROID: 'Android device', KDS: 'Kitchen display',
  KIOSK: 'Self-order kiosk', OTHER: 'Other machine',
};
const ClientIcon = ({ client }) => {
  const Icon = client === 'ANDROID' ? Smartphone : client === 'WEB' ? Globe : Monitor;
  return <Icon size={12} className="text-muted-foreground" />;
};

const Tile = ({ label, value, sub, tone = 'text-foreground' }) => (
  <Card className="shadow-sm">
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </CardContent>
  </Card>
);

const RANGES = [
  [7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [365, 'Last 12 months'],
];

export default function DeviceShiftsReport() {
  const { restaurantId } = useRestaurant();
  const { deviceId } = useParams();
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ rows: null, summary: null, device: null, error: '' });

  const load = useCallback(async (d = days) => {
    if (!restaurantId || !deviceId) return;
    setState((p) => ({ ...p, rows: null, error: '' }));
    try {
      const { rows, summary, device } = await deviceService.sessions(restaurantId, deviceId, { days: d });
      setState({ rows, summary, device, error: '' });
    } catch (e) {
      setState({ rows: [], summary: null, device: null, error: e?.response?.data?.error || e.message });
    }
  }, [restaurantId, deviceId, days]);

  useEffect(() => { load(); }, [load]);

  /* Per-staff totals over exactly the rows below, computed here so they can never disagree with the list. */
  const byStaff = useMemo(() => {
    const map = new Map();
    (state.rows || []).forEach((s) => {
      const key = s.staff_id || `x-${s.staff_name}`;
      const e = map.get(key) || {
        name: s.staff_name, shifts: 0, minutes: 0, onShift: false, last: null,
      };
      e.shifts += 1;
      if (s.minutes) e.minutes += s.minutes;
      if (s.open) e.onShift = true;
      if (s.login_at && (!e.last || s.login_at > e.last)) e.last = s.login_at;
      map.set(key, e);
    });
    return [...map.values()].sort((a, b) => b.minutes - a.minutes);
  }, [state.rows]);

  // Rows come newest-first; grouping keeps that order inside each day.
  const byDay = useMemo(() => {
    const map = new Map();
    (state.rows || []).forEach((s) => {
      const key = dayKey(s.login_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return [...map.entries()];
  }, [state.rows]);

  /* The two facts a manager asks next, and both are already in the rows:
     the single longest shift on this terminal, and the day it worked hardest.
     An open shift has no length yet, so it cannot be the longest. */
  const longest = useMemo(() => {
    let best = { minutes: null, name: '' };
    (state.rows || []).forEach((s) => {
      if (s.open || s.minutes == null) return;
      if (best.minutes == null || s.minutes > best.minutes) {
        best = { minutes: s.minutes, name: s.staff_name };
      }
    });
    return best;
  }, [state.rows]);

  const busiest = useMemo(() => {
    let best = { label: '—', count: 0, minutes: 0 };
    byDay.forEach(([key, list]) => {
      const minutes = list.reduce((t, s) => t + (s.minutes || 0), 0);
      // Shifts first, then hours: a day of six short sittings is busier than
      // one long one, which is what "busiest" means on a counter.
      if (list.length > best.count || (list.length === best.count && minutes > best.minutes)) {
        const d0 = key === '—' ? null : new Date(`${key}T00:00:00`);
        best = {
          label: d0 ? d0.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—',
          count: list.length,
          minutes,
        };
      }
    });
    return best;
  }, [byDay]);

  const d = state.device;

  return (
    /* Full width: this is a ledger, and the two tables below carry six
       columns each. Capped at max-w-5xl it left half a wide screen empty
       while the rows it was meant to show were the ones being squeezed. */
    <div className="p-6 w-full">
      <header className="mb-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight leading-none">
              {d?.name || 'Terminal'}
            </h1>
            {/* A shift report read on its own has to say WHICH terminal, in
                the words a manager uses for it — the branch and what kind of
                machine it is — not only its hardware id. */}
            <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              {d?.status === 'ACTIVE' && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5
                                  text-[11px] font-semibold ${d?.is_online
                    ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${d?.is_online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  {d?.is_online ? 'Live now' : 'Offline'}
                </span>
              )}
              {d?.location_name && <span>{d.location_name}</span>}
              {d?.device_type && <span>· {DEVICE_LABEL[d.device_type] || d.device_type}</span>}
              {d?.hostname && <span>· {d.hostname}</span>}
              {d?.app_version && <span>· app {d.app_version}</span>}
              <span className="font-mono break-all">· {d?.fingerprint || ''}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/settings/devices"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-secondary/50"
            >
              <ArrowLeft size={14} /> All devices
            </Link>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button
              type="button"
              onClick={() => load()}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-secondary/50"
            >
              <RefreshCw size={14} className={state.rows === null ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
        </div>
      </header>

      {state.error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{state.error}</div>
      )}

      {state.rows === null ? (
        <p className="text-sm text-muted-foreground py-16 text-center">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading shifts…
        </p>
      ) : (
        <>
          {state.summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
              <Tile label="Shifts" value={state.summary.sessions} sub={`in the last ${state.summary.days} days`} />
              <Tile label="Staff" value={state.summary.staff} sub="different people" />
              <Tile label="Hours" value={state.summary.hours} sub="worked on this terminal" />
              <Tile label="On shift now" value={state.summary.open_now}
                tone={state.summary.open_now ? 'text-emerald-600' : 'text-foreground'}
                sub={state.summary.open_now ? 'currently signed in' : 'nobody signed in'} />
              {/* Worked out here rather than asked of the server: the rows that
                  answer them are already on the page. */}
              <Tile label="Longest shift" value={fmtDuration(longest.minutes)}
                sub={longest.name || 'no shift recorded'} />
              <Tile label="Busiest day" value={busiest.label}
                sub={busiest.count ? `${busiest.count} shift${busiest.count === 1 ? '' : 's'} · ${fmtDuration(busiest.minutes)}` : 'nothing yet'} />
            </div>
          )}

          {(state.rows || []).length === 0 ? (
            <Card>
              <CardContent className="py-14 text-center text-muted-foreground">
                <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
                No one has signed in on this terminal in this period.<br />
                Shifts are recorded from the moment a staff member signs in on the machine.
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Who worked it, totalled - the question a manager brings here, with the day-by-day ledger as evidence. */}
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Users size={14} /> By staff member
              </h2>
              <Card className="overflow-hidden mb-8">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground bg-secondary/40">
                      <tr>
                        <th className="text-left font-medium px-4 py-2.5">Staff</th>
                        <th className="text-right font-medium px-4 py-2.5">Shifts</th>
                        <th className="text-right font-medium px-4 py-2.5">Time worked</th>
                        <th className="text-right font-medium px-4 py-2.5">Average shift</th>
                        <th className="text-right font-medium px-4 py-2.5">Last signed in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byStaff.map((s) => (
                        <tr key={s.name + s.last} className="border-t border-border/60">
                          <td className="px-4 py-2.5 font-medium">
                            {s.name}
                            {s.onShift && (
                              <span className="ml-2 px-2 py-0.5 text-[11px] rounded-full font-medium bg-emerald-100 text-emerald-800">
                                on shift
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{s.shifts}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtDuration(s.minutes)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">
                            {fmtDuration(Math.round(s.minutes / s.shifts))}
                          </td>
                          <td className="px-4 py-2.5 text-right text-muted-foreground whitespace-nowrap">
                            {s.last ? new Date(s.last).toLocaleString([], {
                              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                            }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Every shift, day by day, newest day first. */}
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Timer size={14} /> Every shift
              </h2>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground bg-secondary/40">
                      <tr>
                        <th className="text-left font-medium px-4 py-2.5 min-w-[170px]">Staff</th>
                        <th className="text-left font-medium px-4 py-2.5">Signed in</th>
                        <th className="text-left font-medium px-4 py-2.5">Signed out</th>
                        <th className="text-right font-medium px-4 py-2.5">Time</th>
                        <th className="text-left font-medium px-4 py-2.5">App</th>
                        <th className="text-left font-medium px-4 py-2.5">IP address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byDay.map(([key, list]) => {
                        const mins = list.reduce((t, s) => t + (s.minutes || 0), 0);
                        return (
                          <React.Fragment key={key}>
                            <tr className="bg-muted/50">
                              <td colSpan={6} className="px-4 py-2">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                  {dayLabel(key)}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {' '}· {list.length} shift{list.length === 1 ? '' : 's'} · {fmtDuration(mins)}
                                </span>
                              </td>
                            </tr>
                            {list.map((s) => (
                              <tr key={s.id} className="border-t border-border/60">
                                <td className="px-4 py-2.5 font-medium">{s.staff_name}</td>
                                <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{time(s.login_at)}</td>
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                  {s.open
                                    ? <span className="text-emerald-600 font-medium">still signed in</span>
                                    : <span className="text-muted-foreground">{time(s.logout_at)}</span>}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{fmtDuration(s.minutes)}</td>
                                <td className="px-4 py-2.5">
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <ClientIcon client={s.client} /> {CLIENT_LABEL[s.client] || s.client || '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground">{s.ip_address || '—'}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
